import { H_GAP } from "./mindmapLayout";

/**
 * Connector geometry for mindmap parent -> child edges: rounded elbows.
 *
 * V3's layout grows right-only, so a parent is always on one side of all of its
 * children and every edge is the same shape: leave the parent's facing edge at
 * its vertical CENTER, run a short stem, turn into a shared vertical lane that
 * sits in the gutter just before the children's near edge, then turn again and
 * enter the child's near edge at the child's vertical center.
 *
 * All children of one parent therefore share a single stem origin and a single
 * lane: the trunk-and-branch shape XMind/NotebookLM use. This replaces the
 * per-child S-curves, which departed at fanned-out points along the parent edge
 * and bent at the midpoint of the gap — so with many children the curves crossed
 * each other and swept through sibling boxes.
 *
 * Because the lane sits strictly between the parent's facing edge and the
 * children's near edge, and every y stays between the two anchors, a route can
 * never enter a sibling's box or escape the two nodes' union.
 *
 * Corners are drawn as explicit 45° chamfer segments rather than left to
 * Excalidraw's proportional roundness, which blows short segments up into loops.
 * The caller sets `roundness: null` on the element; this module only produces
 * points. The returned `points` are relative to `from`, starting at [0,0] and
 * ending at the entry offset — a drop-in for `newLinearElement({ points })`.
 */

/** A node box in top-left scene coordinates (matches on-canvas elements). */
export interface ConnectorBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** A point in scene coordinates. */
export interface ConnectorPoint {
  x: number;
  y: number;
}

export interface ConnectorGeometry {
  /** Departure anchor: the parent's facing side edge, at its vertical center. */
  from: ConnectorPoint;
  /** Entry anchor: the child's near side edge, at its vertical center. */
  to: ConnectorPoint;
  /** Route as [x, y] points relative to `from`; first is [0,0]. */
  points: [number, number][];
}

/**
 * Minimum room between the facing side edges for a full stem + lane route.
 * Below it there is no gutter to put a lane in, so the elbow collapses to the
 * midpoint of whatever room there is (and to a plain 3-point elbow, or a
 * straight line when the boxes overlap).
 */
export const CONNECTOR_MIN_GAP = 24;

/**
 * How far a route coordinate may stray outside the two nodes' union bounding
 * box. The rounded-elbow route satisfies this by construction — every x lies
 * between the two anchors' x and every y between their y — but the bound is
 * kept exported and asserted so a future change cannot quietly reintroduce the
 * canvas-wide excursions the old S-curve produced.
 */
export const CONNECTOR_MARGIN = 32;

/** Length of the horizontal stem leaving the parent's edge. */
export const STEM = 16;

/** Nominal corner size; clamped per route to the room actually available. */
export const CORNER_RADIUS = 10;

/**
 * How far back from the child's near edge the vertical lane sits: the middle of
 * the layout's horizontal gutter, so the lane is clear of every sibling box.
 */
const LANE_BACKOFF = H_GAP / 2;

/** Below this |dy| a child counts as same-row and gets a straight line. */
const SAME_ROW_EPSILON = 1;

const clamp = (value: number, lo: number, hi: number): number =>
  Math.max(lo, Math.min(hi, value));

/** Mirroring a 0 offset yields -0, which fails strict [0,0] comparisons. */
const noNegativeZero = (value: number): number => (value === 0 ? 0 : value);

export interface ConnectorGeometryOptions {
  /**
   * @deprecated No-op. It used to spread sibling departures along the parent's
   * edge, but all children now leave from one stem at the parent's vertical
   * center and it is the shared lane that fans them out. Kept so existing
   * callers keep typechecking; the value is ignored.
   */
  departRatio?: number;
}

export const mindmapConnectorGeometry = (
  fromBox: ConnectorBox,
  toBox: ConnectorBox,
  _options?: ConnectorGeometryOptions,
): ConnectorGeometry => {
  const parentCx = fromBox.x + fromBox.width / 2;
  const parentCy = fromBox.y + fromBox.height / 2;
  const childCx = toBox.x + toBox.width / 2;
  const childCy = toBox.y + toBox.height / 2;

  // Travel direction (+1 child on the right, -1 on the left). Ties route right.
  const side = childCx >= parentCx ? 1 : -1;
  const departX = side > 0 ? fromBox.x + fromBox.width : fromBox.x;
  const enterX = side > 0 ? toBox.x : toBox.x + toBox.width;

  const from: ConnectorPoint = { x: departX, y: parentCy };
  const to: ConnectorPoint = { x: enterX, y: childCy };

  const dy = childCy - parentCy;

  // Same row: nothing to turn around, one straight segment across the gutter.
  if (Math.abs(dy) < SAME_ROW_EPSILON) {
    return {
      from,
      to,
      points: [
        [0, 0],
        [enterX - departX, dy],
      ],
    };
  }

  // Work in travel-relative coordinates: u = side * (x - departX), so the route
  // runs 0 -> gap and the left-side case is the right-side case mirrored.
  const gap = side * (enterX - departX);

  // Lane position: the gutter midpoint, kept strictly between the stem end and
  // the child's edge. When the boxes are too close (or overlap) there is no
  // gutter, so fall back to the midpoint of whatever forward room exists.
  const laneU =
    gap >= CONNECTOR_MIN_GAP
      ? clamp(gap - LANE_BACKOFF, STEM + 1, gap - 1)
      : Math.max(gap / 2, 0);

  // Corner size: never bigger than half the vertical run, and never longer than
  // the horizontal room on either side of the lane, so chamfers cannot overlap
  // each other or push a point outside the anchors.
  const radius = Math.max(
    0,
    Math.min(CORNER_RADIUS, Math.abs(dy) / 2, laneU, gap - laneU),
  );
  const vSign = Math.sign(dy);

  const route: ConnectorPoint[] = [
    { x: 0, y: parentCy },
    { x: laneU - radius, y: parentCy },
    { x: laneU, y: parentCy + radius * vSign },
    { x: laneU, y: childCy - radius * vSign },
    { x: laneU + radius, y: childCy },
    { x: gap, y: childCy },
  ];

  // Degenerate routes (radius 0, no gutter, overlapping boxes) collapse points
  // onto each other; dropping the duplicates leaves a plain 3- or 4-point elbow
  // instead of zero-length segments Excalidraw would render as artifacts.
  const points: [number, number][] = [];
  for (const p of route) {
    const rel: [number, number] = [
      noNegativeZero(side * p.x),
      noNegativeZero(p.y - parentCy),
    ];
    const last = points[points.length - 1];
    if (
      last === undefined ||
      Math.abs(last[0] - rel[0]) > 1e-9 ||
      Math.abs(last[1] - rel[1]) > 1e-9
    ) {
      points.push(rel);
    }
  }

  return { from, to, points };
};
